import i18next from 'i18next';
import { DocTypeCodes, RegistrationDoc, RegistrationDocFile } from '@shared/types/registration';
import {
  compressImageToBudget,
  PDF_BASE64_HARD_LIMIT_BYTES,
  PDF_BASE64_TARGET_BYTES,
  SCAN_BASE64_MAX_BYTES,
  SCAN_BASE64_TARGET_BYTES
} from '@shared/utils/helpers/base64Convertion';
import { compressPdfToBudget, PdfCompressionError, PdfCompressionProgress } from '@shared/utils/helpers/pdfCompression';
import api from '@app/api';
import { notifications } from '@shared/utils/notifications';
import pdfIcon from '@shared/icons/pdf100.png';
import {
  AddScanDoc,
  OsiAccountApplicationDoc,
  OsiAccountApplicationDocFile
} from '@shared/types/osiAccountApplications';
import { queryClient } from '@shared/api/reactQuery';
import { downloadBase64, mimeByExtension } from '@shared/utils/download';
import { getGetApiScansIdQueryOptions } from '@/shared/api/orval/scans/scans';

/**
 * Ошибка подготовки файла к отправке: файл не проходит по размеру.
 *
 * Нужна типизированная ошибка, а не голый throw: вызывающий код должен показать
 * КОНКРЕТНУЮ причину («файл слишком большой» / «не удалось сжать»), а не пустой тост
 * `notifications.error()` без аргумента, который пользователь видит как просто «Ошибка».
 */
export class FileSizeError extends Error {
  /** i18n-ключ сообщения (namespace common) */
  readonly i18nKey: string;

  /** ГРАНИЦА ОТКАЗА в длине base64 — тот самый порог, по которому файл отклонили */
  readonly limitBytes: number;

  constructor(i18nKey: string, limitBytes: number) {
    super(i18nKey);
    this.name = 'FileSizeError';
    this.i18nKey = i18nKey;
    this.limitBytes = limitBytes;
  }
}

/**
 * Человекочитаемый предел для сообщения пользователю.
 * На вход идёт ТОТ ЖЕ порог, по которому файл реально отклонили (граница отказа в длине
 * base64) — иначе в тосте была бы цифра, не совпадающая с фактическим поведением.
 * Пользователь оперирует размером ФАЙЛА, который на треть меньше base64, поэтому показываем
 * limit * 3/4. Единицы КБ/МБ пишутся одинаково в ru и kk.
 */
const formatSizeLimit = (limitBase64Bytes: number): string => {
  const kb = Math.floor((limitBase64Bytes * 0.75) / 1024);
  return kb >= 1024 ? `${(kb / 1024).toFixed(1)} МБ` : `${kb} КБ`;
};

/** Похоже на i18n-ключ вида `namespace:path.to.key` (а не на сырой текст ошибки axios). */
const isI18nKey = (value: string): boolean => /^[a-zA-Z]+:[\w.-]+$/.test(value);

/**
 * Приводит любую ошибку загрузки файла к локализованному тексту для тоста.
 * Раскрывает FileSizeError (с подстановкой предела) и i18n-ключи, которые кладёт
 * в error.message response-интерцептор (например `common:fileTooLargeServer` на 413).
 * Всё остальное — прежний generic «Ошибка», чтобы не показывать техтекст axios.
 */
export const getFileErrorMessage = (e: unknown): string => {
  if (e instanceof FileSizeError) {
    return i18next.t(e.i18nKey, { limit: formatSizeLimit(e.limitBytes) });
  }

  // Ошибки обработки PDF (битый файл, слишком объёмный документ) несут свой i18n-ключ —
  // без этой ветки пользователь увидел бы общий текст «Ошибка». Подстановок у этих текстов
  // нет: они самодостаточны, поэтому t() вызывается без параметров.
  if (e instanceof PdfCompressionError) {
    return i18next.t(e.i18nKey);
  }

  let raw = '';
  if (e instanceof Error) raw = e.message;
  else if (typeof e === 'string') raw = e;

  if (raw && isI18nKey(raw)) return i18next.t(raw);

  return i18next.t('common:error');
};

/**
 * Готовит файл к отправке в AddScanDoc (base64 внутри JSON).
 *
 * Работает с ДВУМЯ разными порогами, их нельзя путать:
 *  - hardLimit — граница отказа: больше неё тело не пролезет через nginx ни при каких условиях;
 *  - target    — цель сжатия изображений: меньше hardLimit, с запасом.
 * Файл между target и hardLimit — нормальный файл, его надо ОТПРАВЛЯТЬ, а не отклонять.
 *
 * У PDF своя ЦЕЛЬ сжатия (PDF_BASE64_TARGET_BYTES = 1 МиБ), а граница отказа с недавних пор
 * общая с картинками — заявленные doc_types.max_size = 2 МиБ. Документ, который не удалось
 * ужать до этого предела, отклоняется: пользователь сжимает его сам.
 *
 * @param maxSizeBytes лимит типа документа с бэка (`requiredDocs[].maxSize` / `dbDocType.maxSize`,
 *   хранится в байтах и относится к размеру ФАЙЛА). Если известен — сужает границу отказа.
 * @param onProgress прогресс клиентского сжатия PDF (страница/всего). Для картинок не вызывается.
 */
export const fileToScanDoc = async (
  file: File,
  docTypeCode?: DocTypeCodes | string,
  maxSizeBytes?: number | null,
  onProgress?: PdfCompressionProgress
): Promise<AddScanDoc> => {
  // maxSize с бэка — лимит на размер ФАЙЛА, а все пороги здесь выражены в длине base64,
  // которая на треть длиннее. Поэтому переводим maxSize в base64-эквивалент (×4/3), а не
  // сравниваем напрямую: прямое сравнение резало бы на треть строже, чем требует сервер.
  const maxSizeAsBase64 = maxSizeBytes && maxSizeBytes > 0 ? Math.floor((maxSizeBytes * 4) / 3) : 0;
  const hardLimitBytes = maxSizeAsBase64 ? Math.min(SCAN_BASE64_MAX_BYTES, maxSizeAsBase64) : SCAN_BASE64_MAX_BYTES;
  // Цель сжатия не может быть выше границы отказа (актуально, если у типа документа
  // maxSize вдруг окажется меньше 700 КБ).
  const targetBytes = Math.min(SCAN_BASE64_TARGET_BYTES, hardLimitBytes);

  let base64data;
  if (file.name.split('.').pop()?.toLowerCase() === 'pdf') {
    // ЦЕЛЬ сжатия PDF — своя (1 МиБ base64, см. PDF_BASE64_TARGET_BYTES): всё, что крупнее,
    // имеет смысл ужать. Раньше целью служил hardLimitBytes (2 МиБ), и документы 1–2 МБ уходили
    // на сервер целиком, хотя ужимаются в 3.5–5 раз за секунду и на максимальном качестве.
    // Если у типа документа лимит ещё меньше — берём его: смысла целиться выше заявленного нет.
    const pdfTargetBytes = Math.min(PDF_BASE64_TARGET_BYTES, hardLimitBytes);
    const compressed = await compressPdfToBudget(file, pdfTargetBytes, onProgress);

    // Граница отказа у PDF своя и заметно выше цели — тело запроса nginx с запасом.
    // Решение пользователя: PDF, который после сжатия больше 2 МБ, но меньше этого предела,
    // надо ОТПРАВЛЯТЬ. Отклоняем только то, что физически не пролезет в тело запроса,
    // иначе nginx вернёт HTML-страницу 413, которую фронт не парсит.
    if (compressed.size > PDF_BASE64_HARD_LIMIT_BYTES) {
      // Растеризацию не запускали: документ требует столько работы, что сжатие заблокировало бы
      // устройство на минуты. Объясняем это, а не «файл велик»: пользователю нужно понять, что
      // делать (разделить документ, отсканировать с меньшим разрешением), а не просто факт отказа.
      if (compressed.skipReason === 'tooHeavy') {
        throw new PdfCompressionError('common:pdfTooHeavy');
      }
      throw new FileSizeError(
        compressed.rasterized ? 'common:pdfCompressFailed' : 'common:fileTooLarge',
        PDF_BASE64_HARD_LIMIT_BYTES
      );
    }

    base64data = compressed.base64;
  } else {
    const compressed = await compressImageToBudget(file, targetBytes);

    // Цель не достигнута — это ещё НЕ отказ. Отказываем, только если результат не проходит
    // даже по границе отказа; иначе отправляем то, что получилось.
    if (!compressed.withinBudget && compressed.size > hardLimitBytes) {
      throw new FileSizeError('common:imageCompressFailed', hardLimitBytes);
    }
    base64data = compressed.base64;
  }

  const scanDoc: AddScanDoc = {
    // Дефолт '' раньше стоял в сигнатуре, но параметр по умолчанию не может идти
    // перед необязательным maxSizeBytes (eslint default-param-last) — перенесён сюда.
    docTypeCode: (docTypeCode ?? '') as string,
    data: base64data,
    extension: file.name.split('.').pop()
  };

  return scanDoc;
};
export const fromDocToFile = async (
  doc: RegistrationDoc | OsiAccountApplicationDoc,
  optimize = false
): Promise<RegistrationDocFile | OsiAccountApplicationDocFile> => {
  const { scan } = doc;

  let docData;
  if (optimize) {
    const scanResponse = await queryClient.fetchQuery(getGetApiScansIdQueryOptions(scan.id ?? 0));
    docData = scanResponse.result;
  } else {
    docData = (await api.Scan(scan.id)) as unknown as string;
  }

  const extension = scan.fileName.split('.').pop() ?? 'unknown';
  let file;
  if (extension === 'pdf') {
    file = await createPdfFile(docData, scan.fileName);
  } else {
    file = await createImgFile(docData, scan.fileName);
  }

  return {
    file,
    preview: createPreview(file),
    fileName: scan.fileName,
    base64data: docData,
    extension,
    ...doc
  };
};
export const createPdfFile = async (base64Data: any, fileName: string): Promise<any> => {
  const fileURL = `data:application/pdf;base64,${base64Data}`;
  const fetchFile = await fetch(fileURL);
  const blobFile = await fetchFile.blob();
  const file = new File([blobFile], fileName);
  return file;
};
export const createImgFile = async (base64Data: any, fileName: string): Promise<any> => {
  const fileURL = `data:image/jpg;base64,${base64Data}`;
  const fetchFile = await fetch(fileURL);
  const blobFile = await fetchFile.blob();
  return new File([blobFile], fileName);
};
export const createPreview = (file: any) => {
  if (file.name.split('.').pop()?.toLocaleLowerCase() === 'pdf') {
    return pdfIcon;
  }
  return URL.createObjectURL(file);
};
export const downloadDoc = async (doc: RegistrationDocFile): Promise<void> => {
  try {
    // iOS/WebKit: data:-URL не скачивается по атрибуту download, а window.open('') + document.write
    // блокируется как popup — обе ветки переведены на blob: через downloadBase64.
    // MIME — из отдельного поля doc.extension: оно гарантированно содержит расширение,
    // тогда как fileName в этой модели может прийти без него.
    downloadBase64(doc.base64data, mimeByExtension(doc.extension), doc.fileName);
  } catch (err: any) {
    notifications.error(err.toString());
  }
};
